import { useEffect, useRef, useCallback } from 'react';
import fetchSRS from '../../../utils/fetchSRS';
import { ReportResponse } from '../../../types/api';
import { Notification } from '@app/components/notifications/Notifications';

export const useReportPolling = ({
    reportDownloadState,
    setReportDownloadState,
    sendNotification,
    clearAllNotifs,
    addReport,
    updateReportDownloading,
    refetchReports,
}: {
    reportDownloadState: {
        loadingReportId?: string;
        reportDownloadingId?: string;
    };
    setReportDownloadState: (state: {
        loadingReportId?: string;
        reportDownloadingId?: string;
    }) => void;
    sendNotification: (
        msg: string,
        opts: Notification['options'],
        shouldAutoclose?: boolean,
    ) => void;
    clearAllNotifs: () => void;
    addReport: (data: any[]) => void;
    updateReportDownloading: (data: any) => void;
    refetchReports: () => void;
}) => {
    const isPolling = useRef(false);
    const notifiedReports = useRef<Record<string, boolean>>({});

    // Memoize the functions to prevent unnecessary re-renders
    const memoizedSendNotification = useCallback(sendNotification, [
        sendNotification,
    ]);
    const memoizedClearAllNotifs = useCallback(clearAllNotifs, [
        clearAllNotifs,
    ]);
    const memoizedAddReport = useCallback(addReport, [addReport]);
    const memoizedUpdateReportDownloading = useCallback(
        updateReportDownloading,
        [updateReportDownloading],
    );
    const memoizedRefetchReports = useCallback(refetchReports, [
        refetchReports,
    ]);
    const memoizedSetReportDownloadState = useCallback(setReportDownloadState, [
        setReportDownloadState,
    ]);

    useEffect(() => {
        const { loadingReportId, reportDownloadingId } = reportDownloadState;
        if (!loadingReportId || !reportDownloadingId || isPolling.current)
            return;

        isPolling.current = true;

        const poll = async () => {
            try {
                if (!notifiedReports.current[reportDownloadingId]) {
                    memoizedSendNotification(
                        'Your report is currently generating!',
                        {
                            context: 'info',
                            description:
                                'The completed report will download to your computer as well as show up in your Reports Page history.',
                        },
                        false,
                    );
                    notifiedReports.current[reportDownloadingId] = true;
                }

                const record = await fetchSRS
                    .url(`/reports/${loadingReportId}`)
                    .get()
                    .json<ReportResponse>();

                if (record?.isProcessing) {
                    setTimeout(poll, 1000);
                    return;
                }

                if (record?.errorType) {
                    const errorType = String(record.errorType).toLowerCase();
                    if (errorType === 'image_error' && record.url) {
                        memoizedSendNotification(
                            typeof record.message === 'string' &&
                                record.message.trim()
                                ? record.message
                                : 'Report generated with missing image(s).',
                            { context: 'warning' },
                        );
                    } else {
                        const errorMessage =
                            typeof record.message === 'string' &&
                            record.message.trim()
                                ? record.message
                                : 'Report generation failed.';
                        throw new Error(errorMessage);
                    }
                }

                const response = await fetch(record.url);
                const blob = await response.blob();
                const ext =
                    record.exportAs?.toLowerCase() === 'pdf' ? 'pdf' : 'pptx';

                const a = document.createElement('a');
                a.href = window.URL.createObjectURL(blob);
                a.download = `${record.name || 'report'}.${ext}`;
                a.click();

                await Promise.all([
                    memoizedAddReport([record]),
                    memoizedUpdateReportDownloading({
                        id: reportDownloadingId,
                        isDownloaded: true,
                        isDownloading: false,
                    }),
                ]);

                memoizedSendNotification('Report downloaded successfully!', {
                    context: 'success',
                });
                await memoizedRefetchReports();
                memoizedClearAllNotifs();
            } catch (e: any) {
                console.error(e);
                memoizedSendNotification(
                    e.message || 'Failed to download the report.',
                    {
                        context: 'danger',
                    },
                );

                await memoizedUpdateReportDownloading({
                    id: reportDownloadingId,
                    isDownloaded: false,
                    isDownloading: false,
                });
            } finally {
                memoizedSetReportDownloadState({});
                isPolling.current = false;
            }
        };

        poll();
    }, [
        reportDownloadState.loadingReportId,
        reportDownloadState.reportDownloadingId,
        memoizedSendNotification,
        memoizedClearAllNotifs,
        memoizedAddReport,
        memoizedUpdateReportDownloading,
        memoizedRefetchReports,
        memoizedSetReportDownloadState,
    ]);
};
